/**
 * bookingContactStorage.ts
 *
 * Pure sessionStorage helpers for the rental booking flow's contact fields
 * (email / first / last / phone). Extracted from the React layer so the
 * round-trip logic is unit-testable with a fake store (no jsdom).
 *
 * Why persist at all: a card decline, an accidental tab reload, or switching
 * to a banking app mid-checkout wipes React state — the customer lands back on
 * an empty form and has to retype four fields on a phone. We keep the contact
 * details in sessionStorage (per-tab, cleared when the tab closes), restore
 * them on mount, and drop them once the booking is confirmed.
 *
 * Keys are tenant-scoped (not product-scoped): contact details are person-level,
 * so a visitor who fails on one product and re-picks another on the same dealer
 * keeps their details. Storage is tab-scoped, so this never leaks across dealers
 * a visitor opens in separate tabs.
 */

export type BookingContact = {
  email: string
  firstName: string
  lastName: string
  phone: string
}

export const EMPTY_CONTACT: BookingContact = { email: '', firstName: '', lastName: '', phone: '' }

/** The subset of the Storage interface we touch — lets tests pass a fake. */
export type ContactStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const NS = 'sifly:booking'

export function contactKey(tenantId: string): string {
  return `${NS}:contact:${tenantId}`
}
export function paymentFailedKey(tenantId: string): string {
  return `${NS}:pay-failed:${tenantId}`
}

export function isContactEmpty(c: BookingContact): boolean {
  return !c.email && !c.firstName && !c.lastName && !c.phone
}

function isBookingContact(v: unknown): v is BookingContact {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return (
    typeof o.email === 'string' &&
    typeof o.firstName === 'string' &&
    typeof o.lastName === 'string' &&
    typeof o.phone === 'string'
  )
}

/**
 * Read the saved contact. Returns null when nothing valid is stored (missing,
 * unparseable, or wrong shape — sessionStorage is untrusted input). Never
 * throws: a locked-down / unavailable store degrades to "no saved value".
 */
export function readContact(store: ContactStore, tenantId: string): BookingContact | null {
  try {
    const raw = store.getItem(contactKey(tenantId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isBookingContact(parsed)) return null
    // Normalize to exactly the four keys — drop any extra properties.
    return {
      email: parsed.email,
      firstName: parsed.firstName,
      lastName: parsed.lastName,
      phone: parsed.phone,
    }
  } catch {
    return null
  }
}

/**
 * Persist the contact. An all-empty contact removes the key rather than storing
 * blanks, so a cleared form doesn't leave a stale entry. Never throws (Safari
 * private mode throws on setItem; we swallow it — persistence is best-effort).
 */
export function writeContact(store: ContactStore, tenantId: string, c: BookingContact): void {
  try {
    if (isContactEmpty(c)) {
      store.removeItem(contactKey(tenantId))
      return
    }
    store.setItem(contactKey(tenantId), JSON.stringify(c))
  } catch {
    /* best-effort */
  }
}

/** Drop the saved contact and any pending payment-failed flag (call on success). */
export function clearContact(store: ContactStore, tenantId: string): void {
  try {
    store.removeItem(contactKey(tenantId))
    store.removeItem(paymentFailedKey(tenantId))
  } catch {
    /* best-effort */
  }
}

/** Record that a payment attempt was declined, so the next mount can explain the return. */
export function markPaymentFailed(store: ContactStore, tenantId: string): void {
  try {
    store.setItem(paymentFailedKey(tenantId), '1')
  } catch {
    /* best-effort */
  }
}

/**
 * One-shot read of the payment-failed flag: returns true if a prior attempt was
 * declined, and clears the flag so the "your payment didn't go through" banner
 * shows exactly once (on the return), not on every subsequent mount.
 */
export function takePaymentFailed(store: ContactStore, tenantId: string): boolean {
  try {
    const hit = store.getItem(paymentFailedKey(tenantId)) === '1'
    if (hit) store.removeItem(paymentFailedKey(tenantId))
    return hit
  } catch {
    return false
  }
}
